// ============================================================================
// authGuard.js — Le jeton Firebase des délégués, rendu fiable
// ============================================================================
// Au login, Aramis reçoit un custom token (functions/auth.js mintAramisToken)
// et ouvre une session Firebase Auth : toutes les Cloud Functions reçoivent
// alors un `request.auth.uid` vérifié, et validateCaller n'a plus à croire
// l'identifiant déclaré par le client (fuite ② de l'audit du 27/08/2026).
//
// Jusqu'ici, cette ouverture de session était SILENCIEUSE en cas d'échec
// (`try { signInWithCustomToken } catch { console.warn }`) : un délégué
// pouvait travailler des semaines en mode « déclaré » sans que personne ne le
// sache. Ce module :
//   1. réessaie l'ouverture de session (3 tentatives, recul progressif) et
//      consigne l'échec dans le stockage local (`aramis_auth_stale`) ;
//   2. vérifie, au démarrage et à chaque retour au premier plan, qu'une session
//      Aramis restaurée est bien adossée à une session Firebase, et rafraîchit
//      le jeton ; sinon, marque la session comme périmée et émet l'événement
//      `aramis:auth-stale` — c'est sur cet événement que se branchera la
//      demande de reconnexion, le jour où l'interrupteur serveur
//      (securiteConfig/authEnforcement) passera en « strict ».
// Aucune de ces opérations ne bloque quoi que ce soit : en mode « mesure »
// (défaut), l'effet est purement observable.
// ============================================================================

// File d'interruptions (lot L6) : la bannière de reconnexion s'affiche
// TOUJOURS (sécurité), mais tant qu'elle est là les autres surfaces
// (bienvenue, notifications, installation) attendent leur tour.
import { REGION_FRANCFORT } from "./regionsFonctions";
import { demander as demanderTour, liberer as libererTour } from "./fileInterruptions";

const CLE_STALE = "aramis_auth_stale";
const DELAIS_MS = [0, 400, 1500];

function lireSessionAramis() {
  try {
    const s = localStorage.getItem("aramis_session") || sessionStorage.getItem("aramis_session");
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

export function marquerPerimee(origine, message) {
  try {
    localStorage.setItem(CLE_STALE, JSON.stringify({ ts: Date.now(), origine, message: String(message || "").slice(0, 200) }));
  } catch {}
  try { window.dispatchEvent(new CustomEvent("aramis:auth-stale", { detail: { origine } })); } catch {}
}

export function effacerPerimee() {
  try { localStorage.removeItem(CLE_STALE); } catch {}
}

export function sessionPerimee() {
  try { const v = localStorage.getItem(CLE_STALE); return v ? JSON.parse(v) : null; } catch { return null; }
}

/** Ouvre la session Firebase avec le custom token reçu au login. Réessaie ;
 *  en dernier ressort, consigne l'échec sans jamais bloquer la connexion
 *  Aramis (la session « déclarée » reste fonctionnelle, comme avant). */
export async function connecterJetonFirebase(auth, token, origine = "login") {
  if (!auth || !token) return false;
  let derniere = null;
  const { signInWithCustomToken } = await import("firebase/auth");
  for (const delai of DELAIS_MS) {
    if (delai) await new Promise((r) => setTimeout(r, delai));
    try {
      await signInWithCustomToken(auth, token);
      effacerPerimee();
      // Une session neuve (reconnexion par passkey sur place, par exemple) :
      // les écrans restés sur un refus de la doctrine se rechargent
      // (surSessionRenouvelee ; relecture adverse du 12/10/2026).
      try { window.dispatchEvent(new CustomEvent("aramis:session-renouvelee", { detail: { origine } })); } catch {}
      // Méthode de la dernière connexion : le renouvellement silencieux par
      // Microsoft n'a de sens que pour une session ouverte par SSO — un délégué
      // sur boîte personnelle (mot de passe, passkey) n'a pas de locataire.
      try {
        if (origine === "sso" || origine === "sso-silencieux") localStorage.setItem("aramis_methode_connexion", "sso");
        else if (origine && !/step-up/.test(origine)) localStorage.setItem("aramis_methode_connexion", origine);
      } catch {}
      return true;
    } catch (e) { derniere = e; }
  }
  console.error(`[authGuard] session Firebase non ouverte (${origine}) :`, derniere && derniere.message);
  marquerPerimee(origine, derniere && derniere.message);
  return false;
}

let _renouvellementEnCours = null;
export function methodeConnexion() {
  try { return localStorage.getItem("aramis_methode_connexion") || ""; } catch { return ""; }
}

async function renouvelerParMicrosoft(session, auth) {
  if (!session || !session.email) return false;
  // Seulement pour une session ouverte par Microsoft (ou un compte MSAL déjà
  // mémorisé) : inutile de solliciter Entra pour un compte Gmail ou Orange.
  let compteMsal = false;
  try { const m = await import("./msal"); compteMsal = m.compteMsalEnCache(); } catch {}
  if (methodeConnexion() !== "sso" && !compteMsal) return false;
  if (_renouvellementEnCours) return _renouvellementEnCours;
  _renouvellementEnCours = (async () => {
    try {
      const { jetonMicrosoftSilencieux } = await import("./msal");
      const idToken = await jetonMicrosoftSilencieux({ loginHint: session.email });
      if (!idToken) return false;
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const res = await httpsCallable(getFunctions(undefined, REGION_FRANCFORT), "aramisSsoMicrosoftLogin")({ msIdToken: idToken });
      const d = res && res.data;
      // Le compte Microsoft doit être CELUI de la session ouverte : on renouvelle
      // un jeton, on ne connecte pas quelqu'un d'autre.
      if (!d || !d.ok || !d.firebaseToken || !d.profile || String(d.profile.id) !== String(session.id)) return false;
      return await connecterJetonFirebase(auth, d.firebaseToken, "sso-silencieux");
    } catch (e) {
      console.warn("[authGuard] renouvellement Microsoft :", e && e.message);
      return false;
    } finally { _renouvellementEnCours = null; }
  })();
  return _renouvellementEnCours;
}

let _authPrete = false;
/** La session Firebase a-t-elle DÉJÀ été restaurée ? Lecture synchrone, sans
 *  attendre : sert à distinguer « pas encore prête » (course au démarrage) de
 *  « prête, mais personne » (session réellement absente). Voir mesureEcritures. */
export function authPrete() { return _authPrete; }
/** Attend que Firebase Auth ait restauré sa session (quelques centaines de
 *  millisecondes au chargement d'une page) avant un appel de fonction : sans
 *  cela, le premier appel d'un rechargement part sans jeton — course mesurée
 *  le 10/09/2026 (question « notes de frais » au démarrage). Ne bloque jamais
 *  plus de `delaiMs`, ne jette jamais ; une fois prête, ne coûte plus rien. */
export async function attendreAuthPrete(delaiMs = 4000) {
  if (_authPrete) return true;
  try {
    const { auth } = await import("./firebase");
    const attente = auth.authStateReady
      ? auth.authStateReady()
      : import("firebase/auth").then(({ onAuthStateChanged }) => new Promise((res) => { const u = onAuthStateChanged(auth, () => { u(); res(); }); }));
    await Promise.race([attente, new Promise((res) => setTimeout(res, delaiMs))]);
    _authPrete = true;
    return true;
  } catch { return false; }
}

/** Une session Aramis est-elle adossée à une session Firebase vivante ?
 *  Rafraîchit le jeton au passage (le SDK le fait seul, on force le contrôle). */
export async function verifierSessionFirebase() {
  try {
    const { auth } = await import("./firebase");
    if (auth.authStateReady) await auth.authStateReady();
    else {
      const { onAuthStateChanged } = await import("firebase/auth");
      await new Promise((res) => { const u = onAuthStateChanged(auth, () => { u(); res(); }); });
    }
    const session = lireSessionAramis();
    if (!session || !session.id) return { ok: true, motif: "sans-session" };
    if (auth.currentUser) {
      try { await auth.currentUser.getIdToken(); } catch (e) { console.warn("[authGuard] rafraîchissement :", e && e.message); }
      effacerPerimee();
      return { ok: true };
    }
    // Deuxième barreau : une session Microsoft vivante renouvelle le jeton
    // sans interaction (msal.js). Jamais de changement d'identité en silence.
    if (await renouvelerParMicrosoft(session, auth)) return { ok: true, motif: "renouvele-microsoft" };
    if (!sessionPerimee()) marquerPerimee("garde", "session Aramis sans session Firebase");
    return { ok: false, motif: "sans-jeton" };
  } catch (e) {
    console.warn("[authGuard] vérification impossible :", e && e.message);
    return { ok: true, motif: "indetermine" };
  }
}

/** Une erreur de Cloud Function est-elle le DÉFI du mode « strict » ? */
export function estDefiAuth(e) {
  const code = String((e && e.code) || "");
  const msg = String((e && e.message) || "");
  return /unauthenticated/i.test(code) && /AUTH_CHALLENGE/.test(msg);
}

// ── Connexion forte exigée (doctrine d'Enio, décision de Pierre du 12/10/2026) ──
// Les fonctions doctrine, lfDoctrine et notesInstitutionnelles refusent une
// session qui n'a pas été ouverte par une passkey (functions/lib/connexionForte.js) :
// code « permission-denied », message préfixé « DOCTRINE_CONNEXION_FORTE : »,
// « LF_CONNEXION_FORTE : » ou « NOTE_CONNEXION_FORTE : », détail
// { raison: "connexion_forte_requise" }. Ce n'est pas un défi du mode strict
// (la session est valide) : l'écran montre CE message, et la bannière propose
// de se reconnecter avec sa passkey.
export const MESSAGE_CONNEXION_FORTE = "La doctrine exige une connexion par passkey (Face ID, Touch ID ou clé) : reconnectez-vous avec votre passkey.";
const RAISON_CONNEXION_FORTE = "connexion_forte_requise";

/** Une erreur de Cloud Function est-elle le refus « connexion forte » ? */
export function estRefusConnexionForte(e) {
  if (!e) return false;
  const details = e.details || (e.origine && e.origine.details) || null;
  if (details && details.raison === RAISON_CONNEXION_FORTE) return true;
  const code = String(e.code || "");
  const msg = String(e.message || "");
  return /permission-denied|^refus/.test(code) && /^[A-Z]+_CONNEXION_FORTE\b/.test(msg);
}

/** À appeler dans le `catch` d'un appel de Cloud Function : si c'est un défi,
 *  la session est marquée périmée et la bannière de reconnexion s'affiche.
 *  Retourne true si le défi a été pris en charge.
 *  Le refus « connexion forte » (doctrine) affiche, lui, la bannière de la
 *  passkey et rend FALSE : l'appelant montre le message du serveur (celui de
 *  la doctrine), jamais celui d'une session expirée. */
export function signalerSiDefi(e) {
  if (estRefusConnexionForte(e)) { afficherBandeau("connexion-forte"); return false; }
  if (!estDefiAuth(e)) return false;
  marquerPerimee("defi", "AUTH_CHALLENGE");
  return true;
}

/** S'abonne aux sessions Firebase neuves (connecterJetonFirebase réussi).
 *  Rend la fonction de désabonnement. */
export function surSessionRenouvelee(fn) {
  if (typeof window === "undefined" || typeof fn !== "function") return () => {};
  const h = (ev) => { try { fn(ev && ev.detail); } catch {} };
  window.addEventListener("aramis:session-renouvelee", h);
  return () => window.removeEventListener("aramis:session-renouvelee", h);
}

/** Le geste « Se reconnecter avec ma passkey » d'un écran de la doctrine : la
 *  coquille (App.jsx) rejoue la cérémonie sur place, sinon rouvre l'écran de
 *  connexion par-dessus la session. */
export function demanderReconnexionPasskey() {
  try { window.dispatchEvent(new CustomEvent("aramis:reconnecter", { detail: { origine: "connexion-forte" } })); } catch {}
}

// ── Bannière de reconnexion (DOM, hors React : une seule, quel que soit l'écran) ──
const ID_BANDEAU = "aramis-auth-bandeau";
const CLE_REPORT = "aramis_auth_bandeau_reporte";

const REPORT_MS = 10 * 60 * 1000; // « Plus tard » : dix minutes (une heure avant le 10/09/2026)

/** Contenu de la bannière selon l'origine. Un DÉFI reçu du serveur (mode
 *  strict : l'appel a été refusé) ne se reporte pas et remplace une alerte
 *  douce ; une session fragile détectée par la garde se signale sans
 *  promettre que les actions passent. */
export function bandeauPour(origine) {
  if (origine === "retabli") {
    return { origine: "retabli", reportable: false, bouton: "Recharger", act: "recharger",
      texte: "Session rétablie. Rechargez la page quand vous aurez terminé pour retrouver toutes les données." };
  }
  if (origine === "connexion-forte") {
    return { origine: "connexion-forte", reportable: true, texte: MESSAGE_CONNEXION_FORTE };
  }
  if (origine === "defi") {
    return { origine: "defi", reportable: false,
      texte: "Votre session sécurisée a expiré : reconnectez-vous pour continuer, puis recommencez votre dernière action." };
  }
  return { origine: origine || "garde", reportable: true,
    texte: "Votre session sécurisée doit être renouvelée. Reconnectez-vous dès que possible : certaines actions peuvent être refusées d'ici là." };
}

export function afficherBandeau(origine) {
  if (typeof document === "undefined") return;
  const cfg = bandeauPour(origine);
  const existant = document.getElementById(ID_BANDEAU);
  if (existant) {
    if (cfg.origine === "defi" && existant.dataset.origine !== "defi") oterBandeau(existant);
    else return;
  }
  if (cfg.reportable) {
    try { const r = Number(sessionStorage.getItem(CLE_REPORT) || 0); if (r && Date.now() - r < REPORT_MS) return; } catch {}
  }
  const b = document.createElement("div");
  b.id = ID_BANDEAU;
  b.dataset.origine = cfg.origine;
  b.setAttribute("role", cfg.reportable ? "status" : "alert");
  // Le bandeau passe à la ligne sur iPhone : sa RANGÉE DE BOUTONS retombait
  // dans la bande 18-53 px du bas, exactement celle qu'occupent la barre Safari
  // minimisée et la barre d'accueil en PWA — « Se reconnecter » devenait
  // intouchable pile au moment où c'est la seule action possible.
  // Au doigt (lot L11r7), il se pose AU-DESSUS de la barre d'onglets, 10 px
  // plus haut que sa hauteur réservée (--h-onglets, zone sûre et seconde ligne
  // du libellé du module comprises) : il recouvrait ses portes. Même gabarit
  // qu'avant, donc même surface couverte, prise sur la page et plus sur les
  // portes ; sans barre (bureau, iPad en paysage), --h-onglets vaut 0 et rien
  // ne bouge.
  // z-index 10002 (04/10/2026) : juste AU-DESSUS du plein écran de séance et du
  // Jour J (FullScreenShell, 10001), sinon l'appel défié redevenait muet pendant
  // une séance ; toujours sous les feuilles (10003 et plus) et les fenêtres.
  b.style.cssText = "position:fixed;left:50%;bottom:max(calc(18px + env(safe-area-inset-bottom, 0px)), calc(var(--h-onglets, 0px) + 10px));transform:translateX(-50%);z-index:10002;max-width:min(560px,calc(var(--vw100) - 24px));"
    + "background:var(--bg-card,#fff);color:var(--text-main,#0d3a4f);border:1px solid rgba(0,63,89,.18);border-radius:12px;"
    + "box-shadow:0 10px 30px rgba(0,0,0,.18);padding:12px 14px;font:13px/1.45 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;"
    + "display:flex;gap:12px;align-items:center;flex-wrap:wrap";
  // Boutons au plancher de 44 pt AU DOIGT (.btn-sm du socle mobile), et une
  // RANGÉE INSÉCABLE (min-width:max-content) : le bandeau, centré par left:50%,
  // ne dispose que de la moitié de l'écran et prend la largeur minimale de son
  // contenu. Deux boutons libres s'y empilaient sur deux rangées (213 px de
  // haut au téléphone au lieu de 157, recette de la chaîne C) ; la rangée
  // passe désormais d'un bloc sous le texte, et l'élargit un peu au lieu de
  // l'allonger. Rembourrage latéral de 10 px et écart de 6 px : la rangée
  // tient dans les 220 px du texte, et le bandeau retrouve EXACTEMENT son
  // gabarit d'avant L11r5 (250 x 157 px au téléphone, 560 x 83 au bureau),
  // sans recouvrir un pixel de plus de la page ni de la barre d'onglets.
  // « Se reconnecter » est l'ACTION de la coquille (.btn-orange de la feuille
  // globale) : navy le jour, carte claire la nuit (il était blanc sur orange,
  // 2,25:1). « Plus tard » est le bouton discret partagé (.btn-ghost).
  b.innerHTML = '<span style="flex:1;min-width:220px"></span>'
    + '<div style="display:flex;gap:6px;min-width:max-content">'
    + '<button type="button" data-act="reconnecter" class="btn btn-sm btn-orange" style="padding-left:10px;padding-right:10px">Se reconnecter</button>'
    + (cfg.reportable ? '<button type="button" data-act="reporter" class="btn btn-sm btn-ghost" style="padding-left:10px;padding-right:10px">Plus tard</button>' : "")
    + "</div>";
  b.querySelector("span").textContent = cfg.texte;
  // Variante d'un bandeau (« Session rétablie » : Recharger).
  if (cfg.act) { const bt = b.querySelector('[data-act="reconnecter"]'); if (bt) { bt.setAttribute("data-act", cfg.act); bt.textContent = cfg.bouton || bt.textContent; } }
  b.addEventListener("click", (ev) => {
    const act = ev.target && ev.target.getAttribute && ev.target.getAttribute("data-act");
    if (act === "recharger") { oterBandeau(b); libererTour("session"); try { window.location.reload(); } catch {} return; }
    if (act === "reconnecter") { oterBandeau(b); libererTour("session"); try { window.dispatchEvent(new CustomEvent("aramis:reconnecter", { detail: { origine: cfg.origine } })); } catch {} }
    if (act === "reporter") { oterBandeau(b); libererTour("session"); try { sessionStorage.setItem(CLE_REPORT, String(Date.now())); } catch {} }
  });
  document.body.appendChild(b);
  poserHauteurBandeau(b);
  demanderTour("session");
}
// La pile des toasts (.enio-toasts, feuille globale) monte de la hauteur du
// bandeau tant qu'il est là (--h-bandeau-auth) : au téléphone, posée au même
// endroit, elle recouvrait « Se reconnecter » pendant les 7 s d'un toast qui
// dit justement « Reconnectez-vous » (refus d'un poste de Gouvernance, et la
// soixantaine d'appels qui combinent signalerSiDefi et un toast ; relecture du
// 04/10).
function poserHauteurBandeau(b) {
  try {
    const h = Math.ceil(b.getBoundingClientRect().height || 0);
    document.documentElement.style.setProperty("--h-bandeau-auth", h > 0 ? `${h + 10}px` : "0px");
  } catch {}
}
function oterBandeau(b) {
  if (b) b.remove();
  try { document.documentElement.style.removeProperty("--h-bandeau-auth"); } catch {}
}
function retirerBandeau() { const b = typeof document !== "undefined" && document.getElementById(ID_BANDEAU); if (b) oterBandeau(b); libererTour("session"); }

/** À installer une fois par session Aramis : au démarrage, puis à chaque
 *  retour au premier plan. Retourne la fonction de désinstallation. */
// Session rétablie après une péremption (15/10/2026, lot 1 de la fermeture
// des règles) : les écoutes Firestore lancées SANS session Firebase ont été
// refusées par les règles (`jetonAramis()`) et ne se relancent pas seules. La
// page se recharge donc tout de suite si aucune fenêtre de travail n'est
// ouverte ; sinon un bandeau propose de le faire, sans jamais perdre une
// saisie. Une session renouvelée sans avoir été périmée (passkey de
// confirmation) ne recharge rien.
export function rechargerApresRetablissement({ fenetresOuvertes = () => 0, recharger = () => window.location.reload(), proposer = proposerRechargement } = {}) {
  if (fenetresOuvertes() > 0) { proposer(); return "propose"; }
  recharger();
  return "recharge";
}
function proposerRechargement() { afficherBandeau("retabli"); }

export function installerGardeSession() {
  const onStale = (ev) => afficherBandeau(ev && ev.detail ? ev.detail.origine : undefined);
  window.addEventListener("aramis:auth-stale", onStale);
  let etaitPerimee = !!sessionPerimee();
  const onPerimee = () => { etaitPerimee = true; };
  window.addEventListener("aramis:auth-stale", onPerimee);
  const finRenouvellement = surSessionRenouvelee(() => {
    if (!etaitPerimee) return;
    etaitPerimee = false;
    import("./uxComponents")
      .then((m) => rechargerApresRetablissement({ fenetresOuvertes: m.fenetresDeTravailOuvertes }))
      .catch(() => rechargerApresRetablissement());
  });
  verifierSessionFirebase().then((r) => { if (r && r.ok) retirerBandeau(); });
  const h = () => { if (document.visibilityState === "visible") verifierSessionFirebase().then((r) => { if (r && r.ok) retirerBandeau(); }); };
  document.addEventListener("visibilitychange", h);
  window.addEventListener("focus", h);
  return () => {
    window.removeEventListener("aramis:auth-stale", onStale);
    window.removeEventListener("aramis:auth-stale", onPerimee);
    finRenouvellement();
    document.removeEventListener("visibilitychange", h); window.removeEventListener("focus", h);
    retirerBandeau();
  };
}
